import type { GUID } from '../auth';
import type { Api, ApiConfig } from './apiFactory';
import apiFactory from './apiFactory';

let api: Api;

type NameDetails = {
  id: GUID;
  name: string;
};

export type Organization = NameDetails & {
  tenants: NameDetails[];
};

export type Tenants = (NameDetails & { organizationName: string })[];

type Response = {
  result: Organization[];
};

const getAvailableOrganizationsResultBrand = Symbol(
  'available-organizations-symbol'
);
export type GetAvailableOrganizationsResult = (
  | { type: 'SUCCESS'; context: { organizations: Organization[] } }
  | { type: 'ERROR'; context: { error: Error } }
) & { readonly __brand: typeof getAvailableOrganizationsResultBrand };

export default {
  initialize(config: ApiConfig) {
    api = apiFactory(config, 'Bearer');
  },
  setToken(newToken: string) {
    api.setToken(newToken);
  },
  setBasePath(hostUrl: string) {
    api.setBasePath(hostUrl);
  },
  async getAvailableOrganizations(
    userId: string,
    applicationId: string,
    controller?: AbortController
  ): Promise<GetAvailableOrganizationsResult> {
    try {
      const response: Response = await api.get(
        `/users/${userId}/applications/${applicationId}/organizations`,
        undefined,
        controller ? { signal: controller.signal } : undefined
      );

      return {
        type: 'SUCCESS',
        __brand: getAvailableOrganizationsResultBrand,
        context: { organizations: response.result }
      };
    } catch (error) {
      return {
        type: 'ERROR',
        __brand: getAvailableOrganizationsResultBrand,
        context: { error: error as any }
      };
    }
  },
  async getApplicationsAssignedToTenant(
    tenantId: string,
    controller?: AbortController
  ): Promise<GetApplicationsAssignedToTenantResult> {
    try {
      const response: TenantApplicationsResponse = await api.get(
        `/tenants/${tenantId}/applications`,
        undefined,
        controller ? { signal: controller.signal } : undefined
      );

      return {
        type: 'SUCCESS',
        __brand: getApplicationsAssignedToTenantResultBrand,
        context: { applications: response.result }
      };
    } catch (error) {
      return {
        type: 'ERROR',
        __brand: getApplicationsAssignedToTenantResultBrand,
        context: { error: error as any }
      };
    }
  }
};

type TenantApplicationsResponse = {
  result: Application[];
};

type Application = {
  applicationId: string;
};

const getApplicationsAssignedToTenantResultBrand = Symbol(
  'applications-assigned-to-tenant-symbol'
);
type GetApplicationsAssignedToTenantResult = (
  | { type: 'SUCCESS'; context: { applications: Application[] } }
  | { type: 'ERROR'; context: { error: Error } }
) & { readonly __brand: typeof getApplicationsAssignedToTenantResultBrand };

export type { Application };
